import React, { useState, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPassword } from '../api/services';

// Password validation rules
const PASSWORD_MIN_LENGTH = 8;
// More inclusive regex that accepts all common symbols without unnecessary escapes
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"|,.<>/?~`])[A-Za-z\d!@#$%^&*()_+\-=[\]{};':"|,.<>/?~`]{8,}$/;

const ResetPassword = () => {
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const validateForm = () => {
    if (!email) {
      return 'Email is missing. Please start the process again.';
    }
    if (!otp) {
      return 'OTP is required';
    }
    if (!newPassword || !confirmPassword) {
      return 'Both password fields are required';
    }
    if (newPassword !== confirmPassword) {
      return 'Passwords do not match';
    }
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      return `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
    }
    if (!PASSWORD_REGEX.test(newPassword)) {
      return 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character';
    }
    return null;
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value.trim());
    setError('');
    setSuccess('');
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');
      setSuccess('');

      const validationError = validateForm();
      if (validationError) {
        setError(validationError);
        setLoading(false);
        return;
      }

      try {
        await resetPassword(email, otp, newPassword, confirmPassword);
        setSuccess('Password has been successfully reset!');
        setTimeout(() => navigate('/login'), 2000);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          (err.code === 'ERR_NETWORK'
            ? 'Network error, please try again later'
            : 'Failed to reset password. Please check your OTP and try again.');
        setError(errorMessage);
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [email, otp, newPassword, confirmPassword, navigate]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && !validateForm()) {
      handleSubmit(e);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Reset Password
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please obtain your OneTimePassword OTP from the system administrator. Note: your OTP is valid for 2 hrs.
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError('')}
            role="alert"
            aria-live="assertive"
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess('')}
            role="alert"
            aria-live="polite"
          >
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            id="otp-input"
            label="OTP"
            type="text"
            value={otp}
            onChange={handleInputChange(setOtp)}
            onKeyDown={handleKeyDown}
            required
            margin="normal"
            disabled={loading}
            variant="outlined"
            InputProps={{ sx: { borderRadius: 2 } }}
            helperText="Enter the OTP provided by your admin"
            aria-label="One-time password"
          />
          <TextField
            fullWidth
            id="new-password-input"
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={handleInputChange(setNewPassword)}
            onKeyDown={handleKeyDown}
            required
            margin="normal"
            disabled={loading}
            variant="outlined"
            InputProps={{
              sx: { borderRadius: 2 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="end"
                    sx={{ color: '#6b7280' }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Use 8+ characters with uppercase, lowercase, number, and special character"
            aria-label="New password"
          />
          <TextField
            fullWidth
            id="confirm-password-input"
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={handleInputChange(setConfirmPassword)}
            onKeyDown={handleKeyDown}
            required
            margin="normal"
            disabled={loading}
            variant="outlined"
            InputProps={{
              sx: { borderRadius: 2 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="end"
                    sx={{ color: '#6b7280' }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Must match the new password"
            aria-label="Confirm new password"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              backgroundColor: '#009db9',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#007a8c',
              },
            }}
            disabled={loading || !!validateForm()}
            aria-label="Reset password"
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{ mt: 2, textTransform: 'none' }}
            onClick={() => navigate('/login')}
            disabled={loading}
            aria-label="Back to login"
          >
            Back to Login
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword;